import { useMemo } from "react";
import type { DrawableAssetId } from "../../Drawable/Assets";
import { METAL_BORDER_TEXTURE_ASSETS } from "../../Drawable/Shape/Card/MetalBeveledBorder.ts";
import {
	RARITY_FRAME_ASSETS,
	ALL_RARITY_FRAME_ASSETS,
	type CardFrameRarity,
} from "../../Drawable/Shape/Card/RarityCardFrame.ts";
import { STONE_CARD_ASSETS } from "../../Drawable/Shape/Card/StoneCardFrame.ts";
import {
	ART_STYLE_FRAME_ASSETS,
	type ArtStyleFrame,
} from "../../Drawable/Shape/Card/ArtStyleCardFrame.ts";
import type { Card } from "../../Value";
import { resolveGeneratedAssetUrl } from "./generatedAssetManifest.ts";

const getCardImageAssetId = (card: Pick<Card, "image"> | string): string =>
	`image:${typeof card === "string" ? card : card.image}`;

export function createCardImageId(
	card: (Pick<Card, "image"> & Partial<Pick<Card, "imageHash">>) | string,
): DrawableAssetId {
	const fileName = typeof card === "string" ? card : card.image;
	const hashQuery =
		typeof card === "string" || !card.imageHash || card.imageHash.length === 0
			? ""
			: `?v=${encodeURIComponent(card.imageHash)}`;
	return resolveGeneratedAssetUrl(
		getCardImageAssetId(card),
		`/images/${fileName}${hashQuery}`,
	) as DrawableAssetId;
}

export const useCreateCardDrawableAssetIds = (
	cards: Pick<Card, "id" | "image" | "imageHash">[],
	rarity?: CardFrameRarity | "all",
	artStyle?: ArtStyleFrame,
): DrawableAssetId[] => {
	return useMemo(
		() => [
			...cards.flatMap((card) => (card.image ? [createCardImageId(card)] : [])),
			...METAL_BORDER_TEXTURE_ASSETS.map((assetId) => assetId as DrawableAssetId),
			...STONE_CARD_ASSETS.map((assetId) => assetId as DrawableAssetId),
			...(rarity
				? (rarity === "all"
						? ALL_RARITY_FRAME_ASSETS
						: Object.values(RARITY_FRAME_ASSETS[rarity])
					).map((assetId) => assetId as DrawableAssetId)
				: []),
			...(artStyle
				? Object.values(ART_STYLE_FRAME_ASSETS[artStyle]).map(
						(assetId) => assetId as DrawableAssetId,
					)
				: []),
		],
		[cards, rarity, artStyle],
	);
};
